import deepFreeze from 'deep-freeze-strict';
import { window } from '@lifeomic/phc-web-toolkit/src/util/globals';
import phcWebToolkitUrls, {
  stringifyParams,
  baseEnvUrl,
} from '@lifeomic/phc-web-toolkit/src/util/urls';
import { ADMIN_PATH } from '@lifeomic/phc-admin/src/routes';

type Id = string | number;

const { api, oauth, resources } = phcWebToolkitUrls;

const urls = {
  oauth: { ...oauth },
  api: { ...api },
  resources: { ...resources },
  externalApps: {
    login: (originalUrl: string) =>
      `/login${stringifyParams({
        originalUrl,
      })}`,
  },
  app: {
    roots: {
      project: () => '/project',
      account: () => '/account',
      app: () => '/app',
      // Mostly deprecated in favor of "/account"; however, is still
      // used for maintaining old bookmarks for now
      accounts: () => '/accounts',
    },
    home: () => '/home',
    manageProjects: () => '/manage-projects',
    userProfile: () => '/user',
    login: () => '/login',
    logout: () => '/logout',
    // "/account" prefix
    apiKeys: () => '/account/api-keys',
    features: () => '/account/features',
    usage: () => '/account/usage',
    billing: () => '/account/billing',
    clients: () => '/account/clients',
    manageAccount: () => '/account/manage',
    manageAccountUsers: () => '/account/users',
    groups: (id?: Id) => `/account/groups${id ? '/' + id : ''}`,
    accessControl: (params?: any, id?: Id) =>
      `/account/access-control${id ? '/' + id : ''}${stringifyParams(params)}`,
    educationContent: () => '/account/educationContent',
    ehrs: (id?: Id) => `/account/ehrs${id ? '/' + id : ''}`,
    invitations: () => '/account/invitations',
    createAccount: (force?: boolean) =>
      `/account/create${force ? '/force' : ''}`,
    accountAuditing: () => '/account/auditing',
    // "/project" prefix
    addData: () => '/project/add-data',
    patients: (id?: Id) => `/project/subjects${id ? '/' + id : ''}`,
    members: (id?: Id) => `/project/members${id ? '/' + id : ''}`,
    omicsExplorer: (id?: Id, params?: any) =>
      `/project/subjects${id ? '/' + id : ''}/omics-explorer${stringifyParams(
        params,
      )}`,
    filesRoot: () => `/project/files`,
    files: (params?: any) => `/project/files/list${stringifyParams(params)}`,
    sftp: (username?: string) =>
      `/project/files/sftp${username ? '/' + username : ''}`,
    fileTransfer: (id?: string) =>
      `/project/files/transfer/jobs${id ? '/' + id : ''}`,
    insights: (subPage?: string) =>
      `/project/insights${subPage ? '/' + subPage : ''}`,
    omics: {
      root: () => '/project/omics',
      dashboard: () => '/project/omics/dashboard',
      addData: () => '/project/omics/add-data',
      tests: (id?: Id, queryString?: any) =>
        `/project/omics/tests${id ? `/${id}` : ''}${
          queryString ? `${stringifyParams(queryString)}` : ''
        }`,
      ingestions: () => '/project/omics/ingestions',
      actions: () => '/project/omics/actions',
      addActions: () => '/project/omics/actions/add',
    },
    ontologies: {
      root: () => '/project/ontologies',
      local: () => '/project/ontologies/local',
      localCodeSystems: () => '/project/ontologies/local/code-systems',
      localCodeSystem: (id?: string) =>
        `/project/ontologies/local/code-system${id ? `/${id}` : ''}`,
      localValueSets: () => '/project/ontologies/local/value-sets',
      localValueSet: (id?: string) =>
        `/project/ontologies/local/value-set${id ? `/${id}` : ''}`,
      marketplace: () => '/project/ontologies/marketplace',
      marketplaceCodeSystems: () =>
        '/project/ontologies/marketplace/code-systems',
      marketplaceCodeSystem: (id?: string) =>
        `/project/ontologies/marketplace/code-system${id ? `/${id}` : ''}`,
      marketplaceValueSets: () => '/project/ontologies/marketplace/value-sets',
      marketplaceValueSet: (id?: string) =>
        `/project/ontologies/marketplace/value-set${id ? `/${id}` : ''}`,
    },
    projectKnowledge: (subPage?: any, id?: Id) => {
      const baseUri = '/project/knowledge';
      const subPagePath = subPage ? `/${subPage}` : '';
      const idPath = id ? `/${id}` : '';
      return `${baseUri}${subPagePath}${idPath}`;
    },
    cohorts: () => '/project/cohorts',
    surveys: (id?: Id) => `/project/surveys${id ? `/${id}` : ''}`,
    projects: () => '/project/projects',
    projectLandingPage: (id: Id) => `/project/projects/${id}`,
    projectSettings: (id: Id) => `/project/projects/${id}/settings`,
    genomeViewer: (params: any) =>
      `${window.location.protocol}//${
        window.location.host
      }/genome-viewer${stringifyParams(params)}`,
    customApps: () => '/project/custom-apps',
    admin: () => ADMIN_PATH,
    tasks: (id?: Id) => `/project/tasks${id ? '/' + id : ''}`,
    consentManagement: {
      project: (params?: any) =>
        `/project/manage-consent${stringifyParams(params)}`,
    },
    circles: (id?: Id) => `/project/circles${id ? '/' + id : ''}`,
    consent: () => '/project/consent',
    dicomViewer: (fileId: any) =>
      `${window.location.protocol}//${window.location.host}/dicom-viewer${
        fileId ? `/${fileId}` : ''
      }`,
    appSurveys: (id?: Id) => `/app/project-tasks/surveys${id ? `/${id}` : ''}`,
    appConsent: () => '/app/project-tasks/consent',
    appInvites: () => '/app/project-tasks/accept-invite',
    appMyInfo: () => '/app/project-tasks/my-info',
    transcription: () => '/project/transcription',
    trialMatching: {
      root: () => '/project/trial-matching',
    },
    contentManagement: {
      root: () => '/project/content-management',
    },
    ml: {
      root: () => '/project/ml',
      modelDetail: (id: string) => `/project/ml/models/${id}`,
      modelRunDetail: (modelId: string, runId: string) =>
        `/project/ml/models/${modelId}/runs/${runId}`,
      datasetDetail: (id: string) => `/project/ml/datasets/${id}`,
      datasetExample: (datasetId: string, exampleId: string) =>
        `/project/ml/datasets/${datasetId}/examples/${exampleId}`,
      datasetLabeling: (datasetId: string) =>
        `/project/ml/datasets/${datasetId}/labeling`,
    },
    marketplace: {
      embedded: () => '/marketplace',
    },
    EMAs: {
      root: () => `/project/emas`,
      manage: () => `/project/emas/manage`,
      create: () => `/project/emas/create`,
    },
    reseller: () => '/reseller',
    workflows: {
      root: () => '/project/workflows',
      onboardingSnapshot: () => '/project/workflows/member-onboarding-snapshot',
      fileValidation: () => '/project/workflows/file-validation',
      regenerativeMedicineOps: () =>
        '/project/workflows/regenerative-medicine-ops',
      /** Previous path; use only for redirects from bookmarks. */
      zoriPulsePrioritiesLegacy: () =>
        '/project/workflows/zori-pulse-priorities',
      reportTemplates: (templateId?: string) =>
        `/project/workflows/report-templates${
          templateId ? `/${templateId}` : ''
        }`,
      zoomAppointments: () => '/project/workflows/zoom/appointments',
      zoriRules: (
        tab?:
          | 'rules'
          | 'all'
          | 'active'
          | 'inactive'
          | 'dry-run'
          | 'knowledge-base'
          | 'clinical-reports'
          | 'codings'
          | 'document-types'
          | 'guidance'
          | 'dex-guidance-review'
          | 'zori-pulse'
          | 'pulse-guidance'
          | 'auto-scan'
          | 'tasks'
          | 'rule-actions'
          | 'document-matchers'
          | 'stats'
          | 'feedback'
          | 'agent-stats'
          | 'user-stats',
        params?: { query?: string; system?: string },
      ) => {
        const base = `/project/workflows/zori-ai${tab ? `/${tab}` : ''}`;
        if (!params) {
          return base;
        }
        const search = new URLSearchParams();
        if (params.query) {
          search.set('query', params.query);
        }
        if (params.system) {
          search.set('system', params.system);
        }
        const qs = search.toString();
        return qs ? `${base}?${qs}` : base;
      },
      /**
       * Clinical Reports groups the document matcher, document type, and
       * extraction guidance admin tools under one Zori AI section. Omitting
       * `subTab` lands on the section, which forwards to the first sub tab the
       * viewer is allowed to open.
       */
      zoriClinicalReports: (
        subTab?: 'document-matchers' | 'document-types' | 'extraction-guidance',
      ) =>
        `/project/workflows/zori-ai/clinical-reports${
          subTab ? `/${subTab}` : ''
        }`,
      /**
       * Stats groups feedback review, agent stats, and user stats under one
       * Zori AI section. Omitting `subTab` lands on the section, which forwards
       * to the first sub tab.
       */
      zoriStats: (subTab?: 'feedback' | 'agent-stats' | 'user-stats') =>
        `/project/workflows/zori-ai/stats${subTab ? `/${subTab}` : ''}`,
      /**
       * Zori Pulse groups pulse guidance and pulse auto-scan under one Zori AI
       * section. Omitting `subTab` lands on the section, which forwards to the
       * first sub tab the viewer is allowed to open.
       */
      zoriPulse: (subTab?: 'pulse-guidance' | 'auto-scan') =>
        `/project/workflows/zori-ai/zori-pulse${subTab ? `/${subTab}` : ''}`,
      /** Previous path segment; use only for redirects from bookmarks. */
      zoriRulesLegacy: (
        tab?:
          | 'rules'
          | 'all'
          | 'active'
          | 'inactive'
          | 'knowledge-base'
          | 'codings'
          | 'document-types'
          | 'guidance'
          | 'tasks'
          | 'rule-actions'
          | 'document-matchers',
      ) => `/project/workflows/zori-rules${tab ? `/${tab}` : ''}`,
      zoriAudit: () => '/project/workflows/zori-audit',
      zoriChat: () => '/project/workflows/zori-chat',
    },
    zoriPulsePriorities: (
      practitionerId = 'me',
      view?: 'doctor' | 'care-team',
    ) => `/zori-pulse/${practitionerId}${view ? `/${view}` : ''}`,
    onboardingTracker: {
      root: () => '/project/onboarding-members-insights',
    },
    employees: () => '/project/employees',
    // print views
    print: {
      root: () => '/print',
      zoriReport: (impressionId: string) =>
        `/print/zori-report/${impressionId}`,
    },
  },
  geneName: {
    reference: (id: Id) =>
      `https://www.genenames.org/data/gene-symbol-report/#!/hgnc_id/HGNC:${id}`,
    search: (searchTerm: string) =>
      `https://www.genenames.org/tools/search/#!/all?query=${searchTerm}`,
  },
  intogen: {
    search: (gene: string) => `https://www.intogen.org/search?gene=${gene}`,
  },
  ncbi: {
    pubmed: (ids = '') => {
      const splitIds = ids.split(',').filter(i => /^\d+$/.test(i));
      if (splitIds.length === 1) {
        return `https://www.ncbi.nlm.nih.gov/pubmed/${ids}`;
      } else {
        return `https://www.ncbi.nlm.nih.gov/pubmed/?term=${splitIds.join(
          '+',
        )}[uid]`;
      }
    },
    gene: (gene: string) => {
      return `https://www.ncbi.nlm.nih.gov/gene?term=(${gene}[Gene Name]) AND human[Organism]`;
    },
    rsid: (rsid?: Id) => `https://www.ncbi.nlm.nih.gov/snp/${rsid}`,
    clinvar: (clinvarAlleleId: Id) =>
      `https://www.ncbi.nlm.nih.gov/clinvar?term=${clinvarAlleleId}[AlleleID`,
  },
  ucsc: {
    genome: (build: string, chr: any, position: any) => {
      // GRCh37 is not recognized by UCSC and needs to be replaced with the older genome reference hg19.
      let dbValue = build;
      if (build.toLowerCase() === 'grch37') dbValue = 'hg19';
      return `http://genome.ucsc.edu/cgi-bin/hgTracks?db=${dbValue}&position=${chr}:${position}`;
    },
  },
  sanger: {
    cosmicMutation: (cosmicId: Id) =>
      `https://cancer.sanger.ac.uk/cosmic/mutation/overview?id=${cosmicId}`,
    cosmicNcv: (cosmicId: Id) =>
      `https://cancer.sanger.ac.uk/cosmic/ncv/overview?id=${cosmicId}`,
  },
  ensemble: {
    transcript: (transcriptId: Id) =>
      `https://www.ensembl.org/Homo_sapiens/Transcript/Summary?db=core;t=${transcriptId}`,
    gene: (geneId: Id) =>
      `https://www.ensembl.org/Homo_sapiens/Gene/Summary?db=core;g=${geneId}`,
  },
  broadInstitute: {
    variant: (chr: any, position: any, ref: any, alt: any) =>
      `http://gnomad.broadinstitute.org/variant/${chr}-${position}-${ref}-${alt}`,
  },
  jupyterNotebooks: () =>
    `${window.location.protocol}//${window.location.host}/notebooks`,
  // served out of S3 directly for now. Must go back up a level because base
  // path is 'baseEnvUrl/phc' while app store mgmt is at 'baseEnvUrl/static/...'
  appStoreAdmin: () => '/../static/app-store-admin-web/index.html',
  marketplace: {
    home: () => {
      if (window.location.host.startsWith('localhost:')) {
        return 'https://marketplace.dev.lifeomic.com';
      }

      return `${window.location.protocol}//${window.location.host?.replace(
        'apps.',
        'marketplace.',
      )}/`;
    },
  },
};

export { stringifyParams, baseEnvUrl };

export default {
  ...deepFreeze(urls),
};
